import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowLeft, Loader2, Mail, MessageCircle } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import PersonMessageComposer from '../../components/portal/PersonMessageComposer';
import WhatsAppAttachment from '../../components/portal/WhatsAppAttachment';
import WorkspaceLink from '../../components/workspace/WorkspaceLink';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import API_BASE from '../../utils/apiBase';
import {
  staffMessageDirectionLabel,
  staffMessageState,
  summarizeStaffCommunications,
} from './staffCommunicationsHelpers';

const API = API_BASE;

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function safeDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

export default function EmployeeStaffCommunicationsPage() {
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const initialChannel = searchParams.get('channel') === 'whatsapp' ? 'whatsapp' : 'email';
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!employeeId || !token) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/staff-communications`,
        { headers },
      );
      setPayload(response.data || null);
    } catch (requestError) {
      setPayload(null);
      setError(safeDetail(requestError, 'Staff communications could not be loaded'));
    } finally {
      setLoading(false);
    }
  }, [employeeId, headers, token]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = summarizeStaffCommunications(payload);
  const employeeLabel = summary.employee?.display_name || 'Employee';
  const employeeCode = summary.employee?.employee_code || '';



  return (
    <div className="space-y-6" data-testid="staff-communications-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
            <WorkspaceLink to={`/portal/employees/${employeeId}`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Staff profile
            </WorkspaceLink>
          </Button>
          <p className="text-sm font-semibold text-primary">Employees</p>
          <h1 className="text-3xl font-heading font-semibold text-text-primary">Staff communications</h1>
          <p className="mt-1 max-w-3xl text-text-muted">
            This is the staff communication domain. Recruitment history remains in Recruitment Inbox and is not copied into this record.
          </p>
        </div>
      </div>

      {loading ? (
        <Card className="border-[#E4E8EB] shadow-sm">
          <CardContent className="flex items-center gap-2 p-6 text-sm text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading staff communications…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardContent className="flex items-start gap-3 p-5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-semibold text-amber-950">Staff communications unavailable</p>
              <p className="mt-1 text-sm text-amber-900">{error}</p>
              <p className="mt-2 text-xs text-amber-800">
                Applicant communication must remain in Recruitment Inbox until activation is complete.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="border-[#E4E8EB] shadow-sm">
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-lg font-heading">{employeeLabel}</CardTitle>
                  <p className="mt-1 text-sm text-text-muted">
                    {employeeCode ? `Employee code ${employeeCode}` : 'Staff record'}
                    {summary.employee?.status ? ` · ${String(summary.employee.status).replace(/_/g, ' ')}` : ''}
                  </p>
                </div>
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
                  Staff domain
                </Badge>
              </div>
            </CardHeader>
          </Card>

          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
            <Card className="border-[#E4E8EB] shadow-sm" data-testid="staff-communication-history">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-heading">
                  <Mail className="h-5 w-5 text-primary" />
                  Communication history
                </CardTitle>
                <p className="text-sm text-text-muted">Staff-domain email and approved WhatsApp messages after activation.</p>
              </CardHeader>
              <CardContent>
                {summary.messages.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 p-5 text-sm text-text-muted">
                    No staff communication has been recorded yet. Recruitment messages are intentionally not shown here.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {summary.messages.map((message) => {
                      const state = staffMessageState(message);
                      const inbound = String(message?.direction || '').toLowerCase() === 'inbound';
                      const whatsapp = String(message?.channel || '').toLowerCase() === 'whatsapp';
                      return (
                        <div key={message.id} className="rounded-xl border border-slate-200 bg-white p-4">
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <Badge
                              variant="outline"
                              className={
                                whatsapp
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                  : inbound
                                    ? 'border-blue-200 bg-blue-50 text-blue-800'
                                    : 'border-slate-200 bg-slate-50 text-slate-700'
                              }
                            >
                              {staffMessageDirectionLabel(message)}
                            </Badge>
                            <span>{state.label}</span>
                            <span>·</span>
                            <span>{formatDateTime(message.created_at)}</span>
                          </div>
                          <p className="mt-2 font-semibold text-slate-900">{message.subject || '(no subject)'}</p>
                          <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{message.body_text || ''}</p>
                          <WhatsAppAttachment message={message} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-[#E4E8EB] shadow-sm" data-testid="staff-shared-message-composer">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg font-heading">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    Start message
                  </CardTitle>
                  <p className="text-sm text-text-muted">
                    The same personal-message composer is used for applicants and staff. Staff history stays in Staff Communications.
                  </p>
                </CardHeader>
                <CardContent>
                  <PersonMessageComposer
                    domain="staff"
                    employeeId={employeeId}
                    token={token}
                    messages={summary.messages}
                    threadId={summary.thread?.id || null}
                    emailEnabled={summary.emailEnabled}
                    emailReason={summary.emailReason || summary.readOnlyReason}
                    readOnly={summary.readOnly}
                    initialChannel={initialChannel}
                    onSent={load}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
